import { Component, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { UserDetailComponent } from '../user-detail/user-detail.component';
import { SecAdminUserService } from '../../services/sec-admin-user.service';
import { ISecAdminUser } from '../../interfaces/sec-admin-user';
import { ApplicationService } from '../../services/application.service';
import { IApplication } from '../../interfaces/application';
import { AgencyTypeService } from '../../services/agency-type.service';
import { IAgencyType } from '../../interfaces/agency-type';
import { AgencyService } from '../../services/agency.service';
import { IAgency } from '../../interfaces/agency';
import { AgencyUserService } from '../../services/agency-user.service';
import { IAgencyUser } from '../../interfaces/agency-user';
import { AgencyAdminService } from '../../services/agency-admin.service';
import { IAgencyAdmin } from '../../interfaces/agency-admin';
import { UserService } from '../../services/user.service';
import { UserAction } from '../../enums/user-action.enum';
import { IUserDetail } from '../../interfaces/user-detail';
import { IUserDetailResult } from '../../interfaces/user-detail-result';
import { IAddNewAgencyUser } from '../../interfaces/add-new-agency-user';
import { IAddAgencyUser } from '../../interfaces/add-agency-user';
import { IUpdateAgencyUser } from '../../interfaces/update-agency-user';
import { IAddAdmin } from '../../interfaces/add-admin';
import { IUpdateAdmin } from '../../interfaces/update-admin';
import { ConfirmationComponent } from '../shared/confirmation/confirmation.component';
import { IUser } from '../../interfaces/user';

@Component({
    selector: 'app-agency-security',
    templateUrl: './agency-security.component.html',
    styles: [],
    standalone: false
})
export class AgencySecurityComponent implements OnInit {
  @ViewChild("userDetail") userDetail: UserDetailComponent = {} as UserDetailComponent;
  @ViewChild("adminDetail") adminDetail: UserDetailComponent = {} as UserDetailComponent;
  @ViewChild("userConfirmDelete") userConfirmDelete: ConfirmationComponent = {} as ConfirmationComponent;
  @ViewChild("adminConfirmDelete") adminConfirmDelete: ConfirmationComponent = {} as ConfirmationComponent;

  public currentUser: ISecAdminUser;
  public applications: IApplication[];
  public agencyTypes: IAgencyType[];
  public agencies: IAgency[];
  public agencyUsers: IAgencyUser[];
  public agencyAdmins: IAgencyAdmin[];
  public agencyAdminForm: UntypedFormGroup;
  public errors: string[] = new Array<string>();

  public userHelpMessage: string;
  public agencyAdminHelpMessage: string;
  public accountAllowLoginHelpMessage: string;
  public applicationAllowLoginHelpMessage: string;
  public showAllowLogin: boolean;

  private selectedApplication: IApplication;
  private agencyId: number;

  public get applicationTextbox() {
    return this.agencyAdminForm.get("applicationTextbox");
  }

  public get application() {
    return this.agencyAdminForm.get("application");
  }

  public get agencyTypeTextbox() {
    return this.agencyAdminForm.get("agencyTypeTextbox");
  }

  public get agencyType() {
    return this.agencyAdminForm.get("agencyType");
  }

  public get agencyTextbox() {
    return this.agencyAdminForm.get("agencyTextbox");
  }

  public get agency() {
    return this.agencyAdminForm.get("agency");
  }

  constructor(
    private readonly titleService: Title,
    private readonly route: ActivatedRoute,
    private readonly fb: UntypedFormBuilder,
    private readonly secAdminUserService: SecAdminUserService,
    private readonly applicationService: ApplicationService,
    private readonly agencyTypeService: AgencyTypeService,
    private readonly agencyService: AgencyService,
    private readonly agencyAdminService: AgencyAdminService,
    private readonly agencyUserService: AgencyUserService,
    private readonly userService: UserService
  ) { }

  ngOnInit(): void {
    this.titleService.setTitle("Agency Security Management");

    this.agencyAdminForm = this.fb.group({
      applicationTextbox: [""],
      application: [""],
      agencyTypeTextbox: [""],
      agencyType: [""],
      agencyTextbox: [""],
      agency: [""]
    });

    this.currentUser = this.secAdminUserService.getCurrentUser();

    this.applicationService.getAgencyAdminApplications(this.currentUser.username)
      .subscribe(data => {
        this.applications = data;
        if (this.applications.length == 1)
          this.applicationTextbox.setValue(this.applications[0].name);
        else if (this.applications.length > 1) {
          var appParam = parseInt(this.route.snapshot.params["appId"]);
          let application: IApplication = this.applications.find(x => x.id === appParam);
          this.application.setValue(application ? application.id : this.applications[0].id);
        }

        this.selectedApplication = this.applications[0];
        this.userHelpMessage = this.selectedApplication.userHelpMessage;
        this.agencyAdminHelpMessage = this.selectedApplication.agencySecurityHelpMessage;
        this.accountAllowLoginHelpMessage = this.selectedApplication.accountAllowLoginHelpMessage;
        this.applicationAllowLoginHelpMessage = this.selectedApplication.applicationAllowLoginHelpMessage;
        this.showAllowLogin = this.selectedApplication.showAllowLogin;
        this.LoadAgencyTypes(this.selectedApplication.id);
      }, error => {
        this.errors.push(error.message);
      });
  }

  public get canManageAdmins(): boolean {
    if (!this.selectedApplication)
      return false;

    if (this.selectedApplication.agencyAdminManagementRequiresSiteAdmin && !this.currentUser.adminApplicationIds.includes(this.selectedApplication.id))
      return false;

    return true;
  }

  public onApplicationChange(): void {
    let application: IApplication = this.applications.find(x => x.id === this.application.value);
    if (application == null)
      this.agencyTypes = null;

    this.selectedApplication = application;
    this.userHelpMessage = this.selectedApplication.userHelpMessage;
    this.agencyAdminHelpMessage = this.selectedApplication.agencySecurityHelpMessage;
    this.accountAllowLoginHelpMessage = this.selectedApplication.accountAllowLoginHelpMessage;
    this.applicationAllowLoginHelpMessage = this.selectedApplication.applicationAllowLoginHelpMessage;
    this.showAllowLogin = this.selectedApplication.showAllowLogin;
    this.LoadAgencyTypes(this.selectedApplication.id);
  }

  public onAgencyTypeChange(): void {
    let agencyType: IAgencyType = this.agencyTypes.find(x => x.id === this.agencyType.value);
    if (agencyType == null)
      this.agencies = null;

    this.LoadAgencies(this.selectedApplication.id, agencyType.id);
  }

  public onAgencyChange(): void {
    let agency: IAgency = this.agencies.find(x => x.id === this.agency.value);
    if (agency == null) {
      this.agencyUsers = null;
      this.agencyAdmins = null;
    }

    this.agencyId = agency.id;
    this.LoadUsers(this.selectedApplication.id, this.agencyId);
    this.LoadAgencyAdmins(this.selectedApplication.id, this.agencyId);
  }

  public renderAllowLoginUser(allowLogin: boolean, allowAppLogin: boolean) {
    if (!allowLogin)
      return "No (Account)";
    else if (!allowAppLogin)
      return "No (Application)";
    else
      return "Yes";
  }

  public renderAllowLoginAdmin(allowLogin: boolean) {
    if (allowLogin)
      return "Yes";
    else
      return "No";
  }

  public renderRoles(roles: string[]) {
    if (!roles)
      return "";

    return roles.join("<br />");
  }

  private LoadAgencyTypes(appId: number): void {
    this.agencyTypeService.getAgencyAdminAgencyTypes(this.currentUser.username, appId)
      .subscribe(data => {
        this.agencyTypes = data;
        if (this.agencyTypes.length == 1) {
          this.agencyTypeTextbox.setValue(this.agencyTypes[0].name);
          this.LoadAgencies(this.selectedApplication.id, this.agencyTypes[0].id)
        } else if (this.agencyTypes.length > 1) {
          this.agencyTypes.unshift({ id: 0, name: "(all)" });
          this.agencyType.setValue(0);
          this.LoadAgencies(this.selectedApplication.id, 0);
        }
      }, error => {
        this.errors.push(error.message);
      });
  }

  private LoadAgencies(appId: number, agencyTypeId: number): void {
    var nullableAgencyTypeId: number | null = agencyTypeId === 0 ? null : agencyTypeId;
    this.agencyService.getAgencyAdminAgencies(this.currentUser.username, appId, nullableAgencyTypeId)
      .subscribe(data => {
        this.agencies = data;
        if (this.agencies.length == 1)
          this.agencyTextbox.setValue(this.agencies[0].name);
        else if (this.agencies.length > 1)
          this.agency.setValue(this.agencies[0].id);

        this.agencyId = this.agencies[0].id;
        this.LoadUsers(appId, this.agencyId);
        this.LoadAgencyAdmins(appId, this.agencyId);
      }, error => {
        this.errors.push(error.message);
      })
  }

  private LoadUsers(appId: number, agencyId: number): void {
    this.agencyUserService.getAgencyUsers(appId, agencyId)
      .subscribe(data => {
        this.agencyUsers = data;
      }, error => {
        this.errors.push(error.message);
      })
  }

  private LoadAgencyAdmins(appId: number, agencyId: number): void {
    this.agencyAdminService.getAgencyAdmins(appId, agencyId)
      .subscribe(data => {
        this.agencyAdmins = data;
      }, error => {
        this.errors.push(error.message);
      })
  }

  public NewAgencyUser() {
    this.userDetail.open(this.selectedApplication.id, null, true);
  }

  public AgencyUserDetails(username: string) {
    this.userService.getUser(this.selectedApplication.id, username)
      .subscribe(data => {
        var userDetail: IUserDetail = {
          id: data.id,
          username: data.username,
          firstName: data.firstName,
          lastName: data.lastName,
          displayName: data.displayName,
          allowLogin: data.allowLogin,
          allowAppLogin: data.allowAppLogin,
          roleIds: data.roleIds
        }
        this.userDetail.open(this.selectedApplication.id, userDetail, true);
      }, error => {
        this.errors.push(error.message);
      })
  }

  public RemoveAgencyUser(id: string, username: string) {
    this.userConfirmDelete.prompt = "Are you sure you want to remove the User '" + username + "' from the list of Agency Users?";
    this.userConfirmDelete.open(id);
  }
  
  public onUserDeleteConfim(id: string) {
    this.agencyUserService.removeAgencyUser(this.selectedApplication.id, this.agencyId, id)
      .subscribe(() => {
        this.LoadUsers(this.selectedApplication.id, this.agencyId);
      }, error => {
        this.errors.push(error.message);
      })
  }

  public ResetAgencyUserPassword(id: string) {
    this.userService.resetPassword(this.selectedApplication.id, this.selectedApplication.id, id)
      .subscribe(() => {
      }, error => {
        this.errors.push(error.message);
      })
  }

  public NewAgencyAdmin() {
    this.adminDetail.open(this.selectedApplication.id, null, false);
  }

  public AgencyAdminDetails(username: string) {
    this.userService.getUser(this.selectedApplication.id, username)
      .subscribe(data => {
        var userDetail: IUserDetail = {
          id: data.id,
          username: data.username,
          firstName: data.firstName,
          lastName: data.lastName,
          displayName: data.displayName,
          allowLogin: data.allowLogin,
          allowAppLogin: data.allowAppLogin,
          roleIds: data.roleIds
        }
        this.adminDetail.open(this.selectedApplication.id, userDetail, false);
      }, error => {
        this.errors.push(error.message);
      })
  }

  public RemoveAgencyAdmin(id: string, username: string) {
    this.adminConfirmDelete.prompt = "Are you sure you want to remove the User '" + username + "' from the list of Agency Security Administrators?";
    this.adminConfirmDelete.open(id);
  }

  public onAdminDeleteConfim(id: string) {
    this.agencyAdminService.removeAgencyAdmin(this.selectedApplication.id, this.agencyId, id)
      .subscribe(() => {
        this.LoadAgencyAdmins(this.selectedApplication.id, this.agencyId);
      }, error => {
        this.errors.push(error.message);
      })
  }
  
  public ResetAgencyAdminPassword(id: string) {
    this.userService.resetPassword(this.selectedApplication.id, 3 /* Sec Admin */, id)
      .subscribe(() => {
      }, error => {
        this.errors.push(error.message);
      })
  }

  public isUserDuplicate = (userId: string) => {
    return (this.agencyUsers.findIndex(x => x.id == userId) >= 0);
  }

  public isUserAlreadyAssigned = (user: IUser) => {
    return (user.externalAgencyId != null);
  }

  public onUserUpdate(result: IUserDetailResult): void {
    switch (result.action) {
      case UserAction.AddNew: {
        let addNewAgencyUser: IAddNewAgencyUser = {
          username: result.user.username,
          firstName: result.user.firstName,
          lastName: result.user.lastName,
          displayName: result.user.displayName,
          allowLogin: result.user.allowLogin,
          allowAppLogin: result.user.allowAppLogin,
          roleIds: result.user.roleIds
        };
        this.agencyUserService.addAgencyUser(this.selectedApplication.id, this.agencyId, addNewAgencyUser)
          .subscribe(() => {
            this.LoadUsers(this.selectedApplication.id, this.agencyId);
          }, error => {
            this.errors.push(error.message);
          })
        break;
      }
      case UserAction.AddFromExisting: {
        let addAgencyUser: IAddAgencyUser = {
          id: result.user.id,
          allowLogin: result.user.allowLogin,
          allowAppLogin: result.user.allowAppLogin,
          roleIds: result.user.roleIds
        };
        this.agencyUserService.addAgencyUserFromExisting(this.selectedApplication.id, this.agencyId, addAgencyUser)
          .subscribe(() => {
            this.LoadUsers(this.selectedApplication.id, this.agencyId);
            if (this.agencyAdmins.findIndex(x => x.id == result.user.id) >= 0)
              this.LoadAgencyAdmins(this.selectedApplication.id, this.agencyId);
          }, error => {
            this.errors.push(error.message);
          })
        break;
      }
      case UserAction.Update: {
        let updateAgencyUser: IUpdateAgencyUser = {
          username: result.user.username,
          lastName: result.user.lastName,
          firstName: result.user.firstName,
          displayName: result.user.displayName,
          allowLogin: result.user.allowLogin,
          allowAppLogin: result.user.allowAppLogin,
          roleIds: result.user.roleIds
        };
        this.agencyUserService.updateAgencyUser(result.user.id, this.selectedApplication.id, updateAgencyUser)
          .subscribe(() => {
            this.LoadUsers(this.selectedApplication.id, this.agencyId);
            if (this.agencyAdmins.findIndex(x => x.id == result.user.id) >= 0)
              this.LoadAgencyAdmins(this.selectedApplication.id, this.agencyId);
          }, error => {
            this.errors.push(error.message);
          })
        break;
      }
    }
  }

  public isAdminDuplicate = (userId: string) => {
    return (this.agencyAdmins.findIndex(x => x.id == userId) >= 0);
  }

  public isAdminAlreadyAssigned = (user: IUser) => {
    return false;
  }

  public onAdminUpdate(result: IUserDetailResult): void {
    switch (result.action) {
      case UserAction.AddNew: {
        let addAgencyAdmin: IAddAdmin = {
          username: result.user.username,
          firstName: result.user.firstName,
          lastName: result.user.lastName,
          displayName: result.user.displayName,
          allowLogin: result.user.allowLogin
        };
        this.agencyAdminService.addAgencyAdmin(this.selectedApplication.id, 3 /* Sec Admin */, this.agencyId, addAgencyAdmin)
          .subscribe(() => {
            this.LoadAgencyAdmins(this.selectedApplication.id, this.agencyId);
          }, error => {
            this.errors.push(error.message);
          })
        break;
      }
      case UserAction.AddFromExisting: {
        this.agencyAdminService.addAgencyAdminFromExisting(this.selectedApplication.id, this.agencyId, result.user.id, result.user.allowLogin)
          .subscribe(() => {
            this.LoadAgencyAdmins(this.selectedApplication.id, this.agencyId);
            if (this.agencyUsers.findIndex(x => x.id == result.user.id) >= 0)
              this.LoadUsers(this.selectedApplication.id, this.agencyId);
          }, error => {
            this.errors.push(error.message);
          })
        break;
      }
      case UserAction.Update: {
        let updateAgencyAdmin: IUpdateAdmin = {
          username: result.user.username,
          lastName: result.user.lastName,
          firstName: result.user.firstName,
          displayName: result.user.displayName,
          allowLogin: result.user.allowLogin
        };
        this.agencyAdminService.updateAgencyAdmin(result.user.id, this.selectedApplication.id, updateAgencyAdmin)
          .subscribe(() => {
            this.LoadAgencyAdmins(this.selectedApplication.id, this.agencyId);
            if (this.agencyUsers.findIndex(x => x.id == result.user.id) >= 0)
              this.LoadUsers(this.selectedApplication.id, this.agencyId);
          }, error => {
            this.errors.push(error.message);
          })
        break;
      }
    }
  }

  public closeError(error: string): void {
    this.errors.splice(this.errors.indexOf(error), 1);
  }
}
